import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Button, IconButton, Chip, Alert, TextField,
  MenuItem, Grid, Tab, Tabs, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, LinearProgress, Divider, InputAdornment, Switch, FormControlLabel,
  Checkbox, Avatar,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Sms as SmsIcon, Send as SendIcon, Refresh as RefreshIcon,
  Save as SaveIcon, Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon, Search as SearchIcon,
  Group as ClassIcon, Person as StudentIcon, Warning as WarnIcon,
  CheckCircle as OkIcon, ErrorOutline as ErrIcon,
  History as HistoryIcon, Settings as SettingsIcon,
  AccountBalanceWallet as CreditIcon, People as ParentsIcon,
  ContactPhone as PhoneBookIcon, DialpadOutlined as DialpadIcon,
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { useAuth } from '../../context/AuthContext';

// MessagingPage — single-screen SMS composer with three tabs:
//   1. Compose  — pick audience (individual / class / all / owing) →
//                 recipient list → write message → preview → send
//   2. History  — every past SMS with status, delivery, filters
//   3. Settings — SMS gateway API key, sender ID, provider URL
//                 (visible + editable only to super_admin)
//
// The backend endpoints live under /api/sms and every write is
// audited via sms_log.

const AUDIENCES = [
  { key: 'individual',   label: 'One student',            icon: <StudentIcon   fontSize="small" /> },
  { key: 'class',        label: 'One class',              icon: <ClassIcon     fontSize="small" /> },
  { key: 'all_students', label: 'All active students',    icon: <ParentsIcon   fontSize="small" /> },
  { key: 'owing',        label: 'Owing students only',    icon: <WarnIcon      fontSize="small" /> },
  { key: 'contacts',     label: 'Phone book',             icon: <PhoneBookIcon fontSize="small" /> },
  { key: 'custom',       label: 'Custom numbers',         icon: <DialpadIcon   fontSize="small" /> },
];

// Parse a free-form blob (commas, newlines, spaces, semicolons) into
// distinct phone-number chips. Kept dumb — the backend does the real
// normalisation via SmsService.normalizePhone before sending.
function parseCustomNumbers(text) {
  return String(text || '')
    .split(/[\s,;]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

// Rough character-based SMS-part counter (GSM 7-bit assumption).
// 160 chars = 1 part, 306 = 2, 459 = 3, 612 = 4.
function smsParts(body) {
  const len = String(body || '').length;
  if (len === 0) return 0;
  if (len <= 160) return 1;
  return Math.ceil((len - 160) / 153) + 1;
}

export default function MessagingPage() {
  const theme = useTheme();
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  const [tab, setTab] = useState(0);
  const [phoneBookOpen, setPhoneBookOpen] = useState(false);

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 1, md: 2 }, py: { xs: 1, md: 1.5 } }}>
      {/* Compact header — replaces the tall hero banner so the compose
          / history / settings surface can breathe. Title left, phone
          book shortcut right, tabs bolted directly underneath. */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: '#fff', px: { xs: 1.5, md: 2.4 }, py: 1.2,
          display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 34, height: 34 }}>
            <SmsIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.1, fontSize: 16 }}>
              SMS Messaging
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', lineHeight: 1.15 }}>
              Students, classes, owing list, phone book & ad-hoc numbers · SMS Online GH
            </Typography>
          </Box>
          <Button size="small" variant="contained" color="inherit"
            startIcon={<PhoneBookIcon fontSize="small" />}
            onClick={() => setPhoneBookOpen(true)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.92)', color: 'primary.main',
              textTransform: 'none', fontWeight: 700,
              '&:hover': { bgcolor: '#fff' },
            }}>
            Phone Book
          </Button>
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{
            borderBottom: 1, borderColor: 'divider',
            minHeight: 38,
            '& .MuiTab-root': { minHeight: 38, py: 0.6, textTransform: 'none', fontWeight: 700 },
          }}>
          <Tab icon={<SendIcon     sx={{ fontSize: 16 }} />} iconPosition="start" label="Compose" />
          <Tab icon={<HistoryIcon  sx={{ fontSize: 16 }} />} iconPosition="start" label="History" />
          <Tab icon={<SettingsIcon sx={{ fontSize: 16 }} />} iconPosition="start"
            label={isSuper ? 'Gateway' : 'Gateway status'} />
        </Tabs>
        <Box sx={{ p: { xs: 1.2, md: 1.8 } }}>
          {tab === 0 && <ComposeTab onOpenPhoneBook={() => setPhoneBookOpen(true)} />}
          {tab === 1 && <HistoryTab />}
          {tab === 2 && <SettingsTab isSuper={isSuper} />}
        </Box>
      </Card>

      <PhoneBookDialog open={phoneBookOpen} onClose={() => setPhoneBookOpen(false)} />
    </Box>
  );
}

// ── Compose ─────────────────────────────────────────────────────
function ComposeTab({ onOpenPhoneBook }) {
  const theme = useTheme();
  const [audience, setAudience] = useState('all_students');
  const [cla, setCla]           = useState('');
  const [studentInd, setStudentInd] = useState('');
  const [channel, setChannel]   = useState('parent');   // parent | student
  const [classes, setClasses]   = useState([]);
  const [rec, setRec]           = useState({ recipients: [], totals: null });
  const [loadingRec, setLoadingRec] = useState(false);

  // Custom-numbers pad: everything the user types in the ad-hoc box
  // is parsed into a chip list on the fly. Send passes each chip as a
  // free-form recipient — the backend rejects invalid numbers.
  const [customText, setCustomText] = useState('');
  const customPhones = useMemo(() => parseCustomNumbers(customText), [customText]);

  const [msg, setMsg]           = useState('');
  const [busy, setBusy]         = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(new Set()); // student_ind subset
  const [pickMode, setPickMode] = useState('all'); // 'all' or 'selected'

  useEffect(() => {
    API.get('/classes').then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data?.classes || [];
      setClasses(list.map(c => c.class || c.cla).filter(Boolean));
    }).catch(() => setClasses([]));
  }, []);

  const loadRecipients = useCallback(async () => {
    setLoadingRec(true); setPicked(new Set());
    try {
      const params = { audience, channel };
      if (audience === 'class' && cla) params.cla = cla;
      if (audience === 'individual' && studentInd) params.student_ind = studentInd;
      const { data } = await API.get('/sms/recipients', { params });
      setRec({ recipients: data?.recipients || [], totals: data?.totals || null });
      setPickMode('all');
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load recipients'));
    } finally {
      setLoadingRec(false);
    }
  }, [audience, cla, studentInd, channel]);

  useEffect(() => {
    // auto-load for audiences that don't require an extra input.
    // 'custom' has its own recipient source (customPhones) so no
    // /recipients call is issued.
    if (audience === 'custom') { setRec({ recipients: [], totals: null }); return; }
    if (audience === 'all_students' || audience === 'owing' || audience === 'contacts') loadRecipients();
    else if (audience === 'class' && cla) loadRecipients();
    else if (audience === 'individual' && studentInd) loadRecipients();
    else setRec({ recipients: [], totals: null });
  }, [audience, cla, studentInd, channel, loadRecipients]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rec.recipients;
    return rec.recipients.filter(r =>
      (r.student_name || '').toLowerCase().includes(needle)
      || (r.student_ind || '').toLowerCase().includes(needle)
      || (r.class_name  || '').toLowerCase().includes(needle)
      || (r.parent_name || '').toLowerCase().includes(needle)
      || (r.phone_raw   || '').toLowerCase().includes(needle)
    );
  }, [rec.recipients, q]);

  const toggleRow = (ind) => {
    setPicked(prev => {
      const n = new Set(prev);
      n.has(ind) ? n.delete(ind) : n.add(ind);
      return n;
    });
    setPickMode('selected');
  };
  const pickAll  = () => { setPicked(new Set(filtered.filter(r => r.has_phone).map(r => r.student_ind))); setPickMode('selected'); };
  const clearAll = () => { setPicked(new Set()); setPickMode('all'); };

  // Effective list going to the gateway. Custom-numbers audience feeds
  // straight from the chip parser; everything else follows the picked-vs-all
  // logic on the recipient table.
  const effective = useMemo(() => {
    if (audience === 'custom') {
      return customPhones.map((p, i) => ({
        student_ind:  `CUSTOM_${i}`,
        student_name: 'Ad-hoc',
        class_name:   '',
        phone:        p,
        phone_raw:    p,
        has_phone:    true,
      }));
    }
    const all = rec.recipients.filter(r => r.has_phone);
    if (pickMode === 'selected') return all.filter(r => picked.has(r.student_ind));
    return all;
  }, [audience, customPhones, rec.recipients, picked, pickMode]);

  const parts = smsParts(msg);

  const send = async () => {
    if (!msg.trim()) return swal.warning('Type a message first');
    if (effective.length === 0) return swal.warning('No recipients with a phone number');
    setConfirmOpen(false);
    setBusy(true);
    try {
      const kind =
        audience === 'owing'    ? 'fee_reminder' :
        audience === 'contacts' ? 'phone_book'   :
        audience === 'custom'   ? 'ad_hoc'       :
        (effective.length > 1 ? 'broadcast' : 'ad_hoc');
      const { data } = await API.post('/sms/send', {
        message: msg,
        kind,
        recipients: effective.map(r => ({
          phone: r.phone,
          // Only send student_ind for real students — synthetic PB_/CUSTOM_
          // keys don't belong on sms_log.student_ind.
          student_ind: /^(PB|CUSTOM)_/.test(String(r.student_ind || '')) ? null : r.student_ind,
          class_name:  r.class_name || null,
        })),
      });
      swal.success(data?.message || `Sent ${data?.sent}/${data?.total}`);
      // Clear composer only if everything landed cleanly.
      if (data?.failed === 0) { setMsg(''); }
    } catch (e) {
      swal.error(swal.pickError(e, 'Send failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Grid container spacing={2}>
      {/* Left: audience picker + recipient table */}
      <Grid item xs={12} md={7}>
        <Card variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 800, mb: 1.2 }}>Recipients</Typography>

          <Grid container spacing={1.2} sx={{ mb: 1.4 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Audience"
                value={audience} onChange={e => setAudience(e.target.value)}>
                {AUDIENCES.map(a => (
                  <MenuItem key={a.key} value={a.key}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {a.icon}<span>{a.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {audience !== 'custom' && audience !== 'contacts' && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Phone preference"
                  value={channel} onChange={e => setChannel(e.target.value)}>
                  <MenuItem value="parent">Parent phone (fall back to student)</MenuItem>
                  <MenuItem value="student">Student phone (fall back to parent)</MenuItem>
                </TextField>
              </Grid>
            )}
            {audience === 'class' && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Class"
                  value={cla} onChange={e => setCla(e.target.value)}>
                  <MenuItem value=""><em>Pick a class…</em></MenuItem>
                  {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
            )}
            {audience === 'individual' && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Student index number"
                  value={studentInd} onChange={e => setStudentInd(e.target.value.trim())}
                  placeholder="e.g. JESS_20" />
              </Grid>
            )}
            {audience === 'contacts' && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<PhoneBookIcon fontSize="small" />}
                  action={
                    <Button size="small" color="primary" onClick={onOpenPhoneBook}
                      sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Manage
                    </Button>
                  }
                  sx={{ py: 0.4 }}>
                  Sending from the saved phone book. Add a new contact via <b>Manage</b>.
                </Alert>
              </Grid>
            )}
            {audience === 'custom' && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" multiline minRows={3} maxRows={8}
                  label="Numbers"
                  value={customText}
                  onChange={e => setCustomText(e.target.value)}
                  placeholder="Paste or type numbers — one per line, or separate with commas / spaces&#10;e.g. 0244123456, 0209413180" />
                {customPhones.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {customPhones.map((p, i) => (
                      <Chip key={`${p}-${i}`} size="small" label={p}
                        onDelete={() => {
                          const next = [...customPhones];
                          next.splice(i, 1);
                          setCustomText(next.join(', '));
                        }}
                        sx={{ fontFamily: 'monospace' }} />
                    ))}
                  </Box>
                )}
              </Grid>
            )}
          </Grid>

          {audience !== 'custom' && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <TextField size="small" fullWidth placeholder="Filter list…"
                value={q} onChange={e => setQ(e.target.value)}
                InputProps={{ startAdornment:
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
              <Button size="small" onClick={loadRecipients} disabled={loadingRec}
                startIcon={<RefreshIcon />} sx={{ textTransform: 'none', fontWeight: 700 }}>
                Reload
              </Button>
            </Stack>
          )}

          {audience !== 'custom' && rec.totals && (
            <Stack direction="row" spacing={0.6} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.6 }}>
              <Chip size="small" label={`${rec.totals.rows} matched`} />
              <Chip size="small" color="success" label={`${rec.totals.with_phone} with phone`} />
              {rec.totals.without_phone > 0 && (
                <Chip size="small" color="warning" label={`${rec.totals.without_phone} no phone`} />
              )}
              <Box sx={{ flex: 1 }} />
              <Button size="small" onClick={pickAll}   disabled={filtered.length === 0}>Select all</Button>
              <Button size="small" onClick={clearAll}  disabled={picked.size === 0}>Clear</Button>
            </Stack>
          )}

          {audience === 'custom' && customPhones.length === 0 && (
            <Alert severity="warning" icon={<DialpadIcon fontSize="small" />} sx={{ py: 0.4 }}>
              Type or paste at least one phone number above.
            </Alert>
          )}
          {audience === 'custom' && customPhones.length > 0 && (
            <Stack direction="row" spacing={0.6} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.6 }}>
              <Chip size="small" color="primary" label={`${customPhones.length} number${customPhones.length === 1 ? '' : 's'} ready`} />
            </Stack>
          )}

          {loadingRec && (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></Box>
          )}

          {audience !== 'custom' && !loadingRec && filtered.length > 0 && (
            <TableContainer sx={{ maxHeight: 420, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small"
                        indeterminate={picked.size > 0 && picked.size < filtered.filter(r => r.has_phone).length}
                        checked={pickMode === 'selected' && picked.size === filtered.filter(r => r.has_phone).length && filtered.filter(r => r.has_phone).length > 0}
                        onChange={(e) => e.target.checked ? pickAll() : clearAll()} />
                    </TableCell>
                    <TableCell>{audience === 'contacts' ? 'Ref' : 'Index'}</TableCell>
                    <TableCell>{audience === 'contacts' ? 'Contact' : 'Student'}</TableCell>
                    <TableCell>{audience === 'contacts' ? 'Label' : 'Class'}</TableCell>
                    <TableCell>Phone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map(r => (
                    <TableRow key={r.student_ind} hover
                      sx={{ bgcolor: r.has_phone ? undefined : alpha(theme.palette.warning.main, 0.06) }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small"
                          checked={pickMode === 'selected' ? picked.has(r.student_ind) : r.has_phone}
                          disabled={!r.has_phone}
                          onChange={() => toggleRow(r.student_ind)} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.student_ind}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.student_name}</TableCell>
                      <TableCell>{r.class_name || '—'}</TableCell>
                      <TableCell>
                        {r.has_phone
                          ? <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.phone}</Typography>
                          : <Chip size="small" color="warning" label="no phone" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Grid>

      {/* Right: composer + send */}
      <Grid item xs={12} md={5}>
        <Card variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 800, mb: 1.2 }}>Message</Typography>
          <TextField fullWidth multiline minRows={6} maxRows={12}
            placeholder="Type your message…"
            value={msg} onChange={e => setMsg(e.target.value)} />
          <Stack direction="row" spacing={0.8} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.6 }}>
            <Chip size="small" variant="outlined" label={`${msg.length} chars`} />
            <Chip size="small" variant="outlined" label={`${parts} SMS part${parts === 1 ? '' : 's'}`} />
            {parts > 1 && (
              <Chip size="small" color="warning"
                label={`≈ ${parts * effective.length} credits at ${effective.length} recipients`} />
            )}
          </Stack>

          <Divider sx={{ my: 1.6 }} />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Chip color="primary" label={`${effective.length} will receive`} sx={{ fontWeight: 800 }} />
            {pickMode === 'selected' && effective.length !== rec.recipients.filter(r => r.has_phone).length && (
              <Chip size="small" variant="outlined" label="filtered / hand-picked" />
            )}
          </Stack>

          <Button fullWidth variant="contained" size="large" startIcon={<SendIcon />}
            disabled={busy || effective.length === 0 || !msg.trim()}
            onClick={() => setConfirmOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 800, borderRadius: 99, py: 1.3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}>
            {busy ? 'Sending…' : `Send ${msg.trim() && parts > 0 ? `${parts * effective.length}` : ''} SMS`}
          </Button>
        </Card>
      </Grid>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', gap: 1, alignItems: 'center', fontWeight: 800 }}>
          <SmsIcon color="primary" /> Send SMS?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            You are about to send an SMS to <b>{effective.length}</b> recipient(s).
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Each message is <b>{parts} SMS part{parts === 1 ? '' : 's'}</b> (~ <b>{parts * effective.length}</b> credits total).
          </Typography>
          <Alert severity="info" sx={{ mt: 1 }}>
            {msg.slice(0, 240)}{msg.length > 240 ? '…' : ''}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={send} disabled={busy}
            startIcon={busy ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99 }}>
            {busy ? 'Sending…' : 'Yes, send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

// ── History ─────────────────────────────────────────────────────
function HistoryTab() {
  const [rows, setRows]   = useState([]);
  const [q, setQ]         = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [diagRow, setDiagRow] = useState(null);   // row currently shown in the diagnose dialog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const { data } = await API.get('/sms/log', { params });
      setRows(data?.rows || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load history')); }
    finally { setLoading(false); }
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.4 }}>
        <TextField size="small" sx={{ flex: 1 }} placeholder="Search phone / body…"
          value={q} onChange={e => setQ(e.target.value)}
          InputProps={{ startAdornment:
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={status}
          onChange={e => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="queued">Queued</MenuItem>
        </TextField>
        <Button onClick={load} startIcon={<RefreshIcon />} disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Refresh
        </Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 560 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Body</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>By</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={9} align="center"
                sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}>
                No messages yet.
              </TableCell></TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{new Date(r.created_at).toLocaleString()}</TableCell>
                <TableCell><Chip size="small" label={r.kind || '—'} /></TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.to_number}</TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Tooltip title={r.body || ''}>
                    <Typography variant="body2" sx={{
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{r.body || '—'}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>{r.class_name || '—'}</TableCell>
                <TableCell>
                  <Tooltip title={r.provider_handshake || ''}>
                    <span>
                      {r.status === 'sent'   && <Chip size="small" color="success" icon={<OkIcon sx={{ fontSize: 14 }} />} label="sent" />}
                      {r.status === 'failed' && <Chip size="small" color="error"   icon={<ErrIcon sx={{ fontSize: 14 }} />}
                        label={r.provider_handshake ? r.provider_handshake.slice(0, 24) : 'failed'} />}
                      {r.status === 'queued' && <Chip size="small" color="warning" label="queued" />}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>{r.sender_id || '—'}</TableCell>
                <TableCell>{r.sent_by_name || (r.sent_by ? `#${r.sent_by}` : 'system')}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Show gateway response">
                    <IconButton size="small" color="primary" onClick={() => setDiagRow(r)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SmsDiagnoseDialog row={diagRow} onClose={() => setDiagRow(null)} />
    </Box>
  );
}

// ── Diagnose dialog ─────────────────────────────────────────────
// Surfaces the raw provider response captured on sms_log so we can
// see EXACTLY why a message failed — the SMS Online GH handshake
// label + JSON payload it returned. Also lists the common causes so
// the operator can act on the response without needing to read the
// gateway docs.
const COMMON_FAILURES = [
  { match: /INVALID_SENDER|SENDER/i,          hint: 'Sender ID not registered / approved on your SMS Online GH portal. Log in and confirm the sender ID (as shown in the row) is approved. If not, submit it for approval or fall back to a numeric sender.' },
  { match: /INSUFFICIENT|CREDIT/i,            hint: 'Not enough credits on the account — check the Gateway tab balance.' },
  { match: /INVALID_TOKEN|UNAUTHORI/i,        hint: 'API key is wrong or was regenerated. Update it under the Gateway tab.' },
  { match: /INVALID_MESSAGE|LENGTH|CONTENT/i, hint: 'Message body was rejected — check length (very long ones split into parts) and unsupported characters.' },
  { match: /INVALID_DESTINATION|RECIPIENT/i,  hint: 'Recipient number was rejected — verify the phone is in international format (233…).' },
  { match: /ROUTE|UNAVAILABLE|NETWORK/i,      hint: 'Provider route to that network is temporarily down. Retry later.' },
];

function SmsDiagnoseDialog({ row, onClose }) {
  const theme = useTheme();
  if (!row) return null;
  let parsedResponse = row.provider_response;
  try {
    if (typeof parsedResponse === 'string') parsedResponse = JSON.parse(parsedResponse);
  } catch { /* leave as raw string */ }
  const hs = row.provider_handshake || '';
  const hint = COMMON_FAILURES.find(c => c.match.test(hs))?.hint;

  return (
    <Dialog open={!!row} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 1.6, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          {row.status === 'sent' ? <OkIcon /> : <ErrIcon />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            SMS #{row.id} · {row.status}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {row.to_number} · sender “{row.sender_id || '—'}” · {new Date(row.created_at).toLocaleString()}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary',
          fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
          Message body
        </Typography>
        <Card variant="outlined" sx={{ p: 1.2, mb: 2, whiteSpace: 'pre-wrap', fontSize: 12.5 }}>
          {row.body || '—'}
        </Card>

        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary',
          fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
          Gateway handshake
        </Typography>
        <Alert severity={row.status === 'sent' ? 'success' : 'error'} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={700}>
            {hs || (row.status === 'sent' ? 'HSHK_OK' : 'No handshake returned')}
          </Typography>
          {hint && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.6 }}>
              <b>Likely cause:</b> {hint}
            </Typography>
          )}
        </Alert>

        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary',
          fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
          Raw provider response
        </Typography>
        <Box component="pre" sx={{
          m: 0, p: 1.2, borderRadius: 1, bgcolor: '#0f172a', color: '#d1e0f7',
          fontSize: 11.5, lineHeight: 1.45, overflow: 'auto', maxHeight: 260,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
{typeof parsedResponse === 'object'
  ? JSON.stringify(parsedResponse, null, 2)
  : (parsedResponse || '(no response body captured)')}
        </Box>

        {row.provider_message_id && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.4, color: 'text.secondary' }}>
            Provider message ID: <b>{row.provider_message_id}</b>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.4 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Settings ────────────────────────────────────────────────────
function SettingsTab({ isSuper }) {
  const theme = useTheme();
  const [form, setForm] = useState({
    enabled: true, api_key: '', sender_id: '',
    api_base: '', send_path: '', balance_path: '',
  });
  const [effective, setEffective] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [showKey, setShowKey]     = useState(false);

  const [balance, setBalance]     = useState(null);
  const [checkingBal, setCheckingBal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/sms/settings');
      setForm({
        enabled:      String(data['sms.enabled']?.value ?? '1') === '1',
        api_key:      data['sms.api_key']?.value ?? '',
        sender_id:    data['sms.sender_id']?.value ?? '',
        api_base:     data['sms.api_base']?.value ?? '',
        send_path:    data['sms.send_path']?.value ?? '',
        balance_path: data['sms.balance_path']?.value ?? '',
      });
      setEffective(data.__effective || null);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load settings')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // Don't send the masked value back — the backend would then
      // save "••••••••" as the real key.
      const payload = { ...form };
      if (payload.api_key === '••••••••') delete payload.api_key;
      payload['sms.enabled']      = payload.enabled ? '1' : '0';
      payload['sms.api_key']      = payload.api_key;
      payload['sms.sender_id']    = payload.sender_id;
      payload['sms.api_base']     = payload.api_base;
      payload['sms.send_path']    = payload.send_path;
      payload['sms.balance_path'] = payload.balance_path;
      delete payload.enabled; delete payload.api_key; delete payload.sender_id;
      delete payload.api_base; delete payload.send_path; delete payload.balance_path;
      await API.put('/sms/settings', payload);
      swal.success('SMS settings saved');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  const checkBalance = async () => {
    setCheckingBal(true);
    try {
      const { data } = await API.get('/sms/balance');
      setBalance(data);
    } catch (e) {
      setBalance({ ok: false, error: swal.pickError(e, 'Balance check failed') });
    } finally { setCheckingBal(false); }
  };

  if (loading) return <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>;

  return (
    <Box>
      {!isSuper && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          These settings are managed by the system administrator. Contact
          them if the API key or sender ID needs updating.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.6 }}>
              <SettingsIcon color="primary" />
              <Typography sx={{ fontWeight: 800 }}>Gateway configuration</Typography>
              <Box sx={{ flex: 1 }} />
              <FormControlLabel
                control={<Switch checked={!!form.enabled}
                  onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                  disabled={!isSuper} />}
                label={form.enabled ? 'Enabled' : 'Disabled'}
                sx={{ '& .MuiFormControlLabel-label': { fontWeight: 700 } }} />
            </Stack>

            <Grid container spacing={1.4}>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="API key"
                  value={form.api_key}
                  onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                  type={showKey ? 'text' : 'password'}
                  disabled={!isSuper}
                  placeholder="Paste your SMS Online GH API key"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowKey(v => !v)}>
                          {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText="Generate from your SMS Online GH portal (Settings → API Keys)" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Sender ID"
                  value={form.sender_id}
                  onChange={e => setForm(f => ({ ...f, sender_id: e.target.value.slice(0, 11) }))}
                  disabled={!isSuper}
                  helperText="Max 11 chars · pre-registered with provider"
                  inputProps={{ maxLength: 11 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="API base URL"
                  value={form.api_base}
                  onChange={e => setForm(f => ({ ...f, api_base: e.target.value }))}
                  disabled={!isSuper} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Send path"
                  value={form.send_path}
                  onChange={e => setForm(f => ({ ...f, send_path: e.target.value }))}
                  disabled={!isSuper} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Balance path"
                  value={form.balance_path}
                  onChange={e => setForm(f => ({ ...f, balance_path: e.target.value }))}
                  disabled={!isSuper} />
              </Grid>
            </Grid>

            {isSuper && (
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
                <Button onClick={load} disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Discard changes
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={save}
                  disabled={saving}
                  sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99 }}>
                  {saving ? 'Saving…' : 'Save settings'}
                </Button>
              </Stack>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.6 }}>
              <CreditIcon color="secondary" />
              <Typography sx={{ fontWeight: 800 }}>SMS credit balance</Typography>
            </Stack>
            <Button variant="outlined" onClick={checkBalance} disabled={checkingBal}
              startIcon={checkingBal ? <CircularProgress size={16} /> : <RefreshIcon />}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {checkingBal ? 'Checking…' : 'Check balance now'}
            </Button>
            {balance && balance.ok && (
              <Alert severity="success" sx={{ mt: 1.5 }}>
                <b>{balance.balance ?? '—'}</b> {balance.currency || 'credits'} remaining
              </Alert>
            )}
            {balance && !balance.ok && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                <Typography variant="body2" fontWeight={700}>{balance.error}</Typography>
                {balance.raw && (
                  <Box component="pre" sx={{ mt: 1, fontSize: 11, whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word', maxHeight: 160, overflow: 'auto',
                    bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                    {typeof balance.raw === 'string' ? balance.raw : JSON.stringify(balance.raw, null, 2)}
                  </Box>
                )}
              </Alert>
            )}
          </Card>

          {isSuper && <TestSendCard />}

          {effective && (
            <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 800, mb: 1.2 }}>Effective config</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                What the send pipeline will actually use after falling back through
                database → env → hard defaults.
              </Typography>
              <Stack spacing={0.8}>
                <Row label="Enabled"      value={effective.enabled ? 'Yes' : 'No'} />
                <Row label="Sender ID"    value={effective.sender_id} />
                <Row label="API base"     value={effective.api_base} />
                <Row label="Send path"    value={effective.send_path} />
                <Row label="Balance path" value={effective.balance_path} />
                <Row label="API key"      value={effective.has_key ? 'Set' : 'MISSING'} tint={effective.has_key ? 'success' : 'error'} />
              </Stack>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Test Send card ───────────────────────────────────────────
// A "kick the tyres" tool for the SMS Online GH pipeline. Sends a
// real message and shows the FULL provider response — including the
// stringified network error captured by the SmsService catch block
// — so we can pinpoint transport failures before compose-time.
function TestSendCard() {
  const [to, setTo]         = useState('');
  const [msg, setMsg]       = useState('SMS gateway test — please ignore.');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [probing, setProbing] = useState(false);
  const [probe, setProbe]     = useState(null);

  const run = async () => {
    if (!to.trim()) return;
    setRunning(true); setResult(null);
    try {
      const { data } = await API.post('/sms/test-send', { to, message: msg });
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: swal.pickError(e, 'Test send failed'), log: null });
    } finally { setRunning(false); }
  };

  const probeEndpoints = async () => {
    if (!to.trim()) { swal.warning('Enter your own number first — the probe hits every candidate path.'); return; }
    setProbing(true); setProbe(null);
    try {
      const { data } = await API.post('/sms/probe-endpoints', { to, message: msg });
      setProbe(data);
    } catch (e) {
      swal.error(swal.pickError(e, 'Endpoint probe failed'));
    } finally { setProbing(false); }
  };

  const raw = result?.log?.provider_response;
  let parsed = raw;
  try { if (typeof parsed === 'string') parsed = JSON.parse(parsed); } catch {}

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.4 }}>
        <SendIcon color="primary" />
        <Typography sx={{ fontWeight: 800 }}>Diagnostic test send</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.2 }}>
        Fire one real SMS through the gateway and show the raw response body / network error.
      </Typography>
      <Stack spacing={1}>
        <TextField size="small" label="To" placeholder="0244123456 (yours)"
          value={to} onChange={e => setTo(e.target.value)} />
        <TextField size="small" label="Body" multiline minRows={2}
          value={msg} onChange={e => setMsg(e.target.value)} />
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={run} disabled={running || !to.trim()}
            startIcon={running ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99 }}>
            {running ? 'Sending…' : 'Send test'}
          </Button>
          <Button variant="outlined" onClick={probeEndpoints} disabled={probing || !to.trim()}
            startIcon={probing ? <CircularProgress size={14} /> : <SearchIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99 }}>
            {probing ? 'Probing…' : 'Probe endpoints'}
          </Button>
        </Stack>
      </Stack>

      {probe && (
        <Box sx={{ mt: 1.6 }}>
          <Alert severity={probe.candidates?.some(c => c.looks_like_api) ? 'success' : 'warning'} sx={{ mb: 1 }}>
            {probe.hint}
          </Alert>
          <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2, maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Path</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Content-Type</TableCell>
                  <TableCell>Bytes</TableCell>
                  <TableCell>Verdict</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(probe.candidates || []).map(c => (
                  <TableRow key={c.path} hover
                    sx={{ bgcolor: c.looks_like_api ? (t) => alpha(t.palette.success.main, 0.12) : undefined }}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{c.path}</TableCell>
                    <TableCell>{c.status || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{c.content_type || '—'}</TableCell>
                    <TableCell>{c.body_bytes}</TableCell>
                    <TableCell>
                      {c.looks_like_api
                        ? <Chip size="small" color="success" label="API JSON" />
                        : <Chip size="small" color="default" label={c.error || 'catch-all'} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box component="pre" sx={{
            mt: 1.2, m: 0, p: 1.2, borderRadius: 1, bgcolor: '#0f172a', color: '#d1e0f7',
            fontSize: 11, lineHeight: 1.4, overflow: 'auto', maxHeight: 200,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
{JSON.stringify({ root: probe.root_probe, version: probe.version_probe }, null, 2)}
          </Box>
        </Box>
      )}

      {result && (
        <Box sx={{ mt: 1.6 }}>
          <Alert severity={result.ok ? 'success' : 'error'} sx={{ mb: 1 }}>
            {result.ok ? 'Message accepted by gateway.' : (result.error || 'Send failed.')}
            {result.log?.provider_handshake && (
              <Box sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: 12 }}>
                handshake: <b>{result.log.provider_handshake}</b>
              </Box>
            )}
          </Alert>
          {parsed != null && (
            <Box component="pre" sx={{
              m: 0, p: 1.2, borderRadius: 1, bgcolor: '#0f172a', color: '#d1e0f7',
              fontSize: 11.5, lineHeight: 1.45, overflow: 'auto', maxHeight: 240,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
{typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed)}
            </Box>
          )}
        </Box>
      )}
    </Card>
  );
}

function Row({ label, value, tint }) {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="caption" sx={{ minWidth: 90, color: 'text.secondary', fontWeight: 700 }}>
        {label}
      </Typography>
      <Chip size="small"
        color={tint || 'default'} variant={tint ? 'filled' : 'outlined'}
        label={String(value)}
        sx={{ fontFamily: 'monospace', fontSize: 11.5, height: 22 }} />
    </Stack>
  );
}

// ── Phone Book dialog ───────────────────────────────────────────
// Full CRUD for sms_contact. Sits on top of the messaging surface so
// operators can add teacher/worker numbers without leaving the page,
// then switch the Compose audience to "Phone book" to send.
function PhoneBookDialog({ open, onClose }) {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [labelFilter, setLabelFilter] = useState('');
  const [editing, setEditing] = useState(null);   // null | 'new' | contact row
  const [form, setForm] = useState({ name: '', phone: '', label: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q)            params.q = q;
      if (labelFilter)  params.label = labelFilter;
      const { data } = await API.get('/sms/contacts', { params });
      setRows(data?.rows || []);
      setLabels(data?.labels || []);
    } catch (e) { swal.error(swal.pickError(e, 'Could not load contacts')); }
    finally { setLoading(false); }
  }, [q, labelFilter]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const startNew = () => {
    setEditing('new');
    setForm({ name: '', phone: '', label: '', notes: '' });
  };
  const startEdit = (row) => {
    setEditing(row);
    setForm({
      name:  row.name  || '',
      phone: row.phone_raw || row.phone || '',
      label: row.label || '',
      notes: row.notes || '',
    });
  };
  const cancelEdit = () => { setEditing(null); setForm({ name: '', phone: '', label: '', notes: '' }); };

  const save = async () => {
    if (!form.name.trim())  { swal.warning('Name is required'); return; }
    if (!form.phone.trim()) { swal.warning('Phone is required'); return; }
    setBusy(true);
    try {
      if (editing === 'new') {
        await API.post('/sms/contacts', form);
        swal.success('Contact added');
      } else {
        await API.put(`/sms/contacts/${editing.id}`, form);
        swal.success('Contact updated');
      }
      cancelEdit();
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    const ok = await swal.confirm({
      title: 'Delete contact?',
      text: `"${row.name}" (${row.phone}) will be removed from the phone book.`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await API.delete(`/sms/contacts/${row.id}`);
      swal.success('Contact deleted');
      load();
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: '#fff', px: 3, py: 1.6, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)' }}>
          <PhoneBookIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.15 }}>
            Phone Book
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Save teacher, worker or vendor numbers so you can SMS them without keeping them in the student roster.
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={busy} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'grey.50', p: 2 }}>
        {/* Add / edit form */}
        <Card variant="outlined" sx={{ p: 1.6, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2 }}>
            <Typography sx={{ fontWeight: 800, flex: 1 }}>
              {editing === 'new' ? 'New contact' : editing ? `Edit "${editing.name}"` : 'Add a contact'}
            </Typography>
            {editing == null && (
              <Button size="small" variant="contained" startIcon={<AddIcon />}
                onClick={startNew}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 99 }}>
                New
              </Button>
            )}
          </Stack>
          {(editing !== null) && (
            <>
              <Grid container spacing={1.2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Name" autoFocus
                    value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Mr. Kojo, Class 4 Teacher" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Phone"
                    value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="0244…" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Label / group"
                    value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="Teacher, Board, Vendor…" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" multiline maxRows={3} label="Notes (optional)"
                    value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} sx={{ mt: 1.4 }} justifyContent="flex-end">
                <Button onClick={cancelEdit} disabled={busy}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={busy}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 99 }}>
                  {busy ? 'Saving…' : (editing === 'new' ? 'Add contact' : 'Save changes')}
                </Button>
              </Stack>
            </>
          )}
        </Card>

        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.2 }}>
          <TextField size="small" sx={{ flex: 1 }} placeholder="Search name, phone or label…"
            value={q} onChange={e => setQ(e.target.value)}
            InputProps={{ startAdornment:
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Label" value={labelFilter}
            onChange={e => setLabelFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value=""><em>All labels</em></MenuItem>
            {labels.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </TextField>
          <Button onClick={load} startIcon={<RefreshIcon />} disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 700 }}>Refresh</Button>
        </Stack>

        {loading && <LinearProgress sx={{ mb: 1 }} />}

        <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2, maxHeight: 420 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase' } }}>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right" sx={{ width: 96 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center"
                    sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                    No contacts yet. Click <b>New</b> to add one.
                  </TableCell>
                </TableRow>
              )}
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{r.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.phone}</TableCell>
                  <TableCell>
                    {r.label ? <Chip size="small" label={r.label} /> : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Tooltip title={r.notes || ''}>
                      <Typography variant="body2" sx={{
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{r.notes || '—'}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => startEdit(r)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => remove(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.4 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
